/**
 * Shared homepage-hero preload resolver (server-safe — no browser globals).
 *
 * The URL math MUST stay byte-identical with:
 *  - HeroSlider's renderUrl(..., 1920|1366, 50) output (src/components/ui/HeroSlider.tsx)
 *  - HERO_PRELOAD_SCRIPT's mkRender fallback (src/lib/boot-scripts.ts)
 * so the SSR <link rel="preload"> byte-matches the rendered <source srcSet>.
 */

import { optimizedImg } from "@/lib/imageUtils";
import { homepageCdnImage } from "@/lib/homepageImages";

/** Shared Modern sources keep SSR preload and picture selection identical. */
export function modernHeroSources(desktop: string, mobile?: string, optimize = true, homepage = false) {
  const delivery = homepage ? homepageCdnImage : (src: string) => src;
  return {
    mobile: delivery(optimize ? optimizedImg(mobile || desktop, "catalog", { q: 70 }) : mobile || desktop),
    desktop: delivery(optimize ? optimizedImg(desktop || mobile || "", "hero", { q: 70 }) : desktop || mobile || ""),
  };
}

export function modernHomepageHeroPreload(content: { slides?: HeroSlideLike[] } | null | undefined) {
  const first = content?.slides?.find((slide) => slide.image_desktop || slide.image_mobile);
  if (!first) return null;
  return modernHeroSources(first.image_desktop || first.image_mobile || "", first.image_mobile, true, true);
}

const MYRVPART_CDN = "https://www.myrvpart.com/images/splash-assets";
const SPLASH_PREFIX = "/images/splash-assets/";

export interface HeroSlideLike {
  image_desktop?: string | undefined;
  image_mobile?: string | undefined;
}

export const normalizeHeroUrl = (url?: string): string | undefined => {
  if (!url) return url;
  return url.replace(
    /^https?:\/\/(?:www\.)?myrvpart\.com\/images\/splash-assets\//,
    SPLASH_PREFIX,
  );
};

/** Mirrors renderUrl(src, w, q) for the splash-assets/storage URL shapes. */
const mkRender = (u: string | undefined, w: number, q = 50): string | null => {
  if (!u) return null;
  if (u.indexOf(SPLASH_PREFIX) === 0) return u;
  if (u.indexOf("/images/") === 0) return MYRVPART_CDN + u;
  if (u.indexOf("/splash-assets/manifest/") !== -1 && /\.webp(\?|$)/.test(u)) {
    return u.split("?")[0] ?? u;
  }
  if (u.indexOf("/storage/v1/object/public/") !== -1) {
    const base = u
      .replace("/storage/v1/object/public/", "/storage/v1/render/image/public/")
      .split("?")[0];
    return `${base}?width=${w}&quality=${q}&resize=contain&format=webp`;
  }
  return u;
};

/**
 * Mobile hero LCP source. The mobile strip slot is 200px tall and full-width,
 * so 828w at q45 covers a 2x 414px viewport while cutting the raw storage
 * object (~66 KB) down by roughly a third. Used by BOTH the SSR preload and
 * MobileHeroStrip's <img src> so the two byte-match and only one fetch runs.
 */
export const homepageHeroMobileSrc = (url?: string): string | null =>
  mkRender(normalizeHeroUrl(url), 828, 45);

export function homepageHeroMobilePreload(
  content: { slides?: HeroSlideLike[] } | null | undefined,
): string | null {
  const slides = content?.slides;
  if (!slides || slides.length === 0) return null;
  const first = slides.find((s) => s.image_mobile || s.image_desktop);
  if (!first) return null;
  return homepageHeroMobileSrc(first.image_mobile || first.image_desktop);
}

export interface HeroDesktopPreload {
  href: string;
  imageSrcSet: string;
  imageSizes: string;
}

/**
 * Resolve the desktop LCP preload for the homepage CMS hero from loader data.
 * Returns null when no usable slide exists — the boot-script fallback in
 * __root then handles preloading after its own page_content fetch.
 */
export function homepageHeroDesktopPreload(
  content: { slides?: HeroSlideLike[] } | null | undefined,
): HeroDesktopPreload | null {
  const slides = content?.slides;
  if (!slides || slides.length === 0) return null;
  const first = slides.find((s) => s.image_desktop || s.image_mobile);
  if (!first) return null;
  const dtop = normalizeHeroUrl(first.image_desktop);
  const img = dtop || normalizeHeroUrl(first.image_mobile) || SPLASH_PREFIX + "hero-1920.webp";
  const i1366 =
    dtop && dtop.indexOf("/splash-assets/hero-1920.webp") !== -1
      ? SPLASH_PREFIX + "hero-1366.webp"
      : img;
  const h1920 = mkRender(img, 1920);
  const h1366 = mkRender(i1366, 1366);
  if (!h1920 || !h1366) return null;
  return {
    href: h1920,
    imageSrcSet: `${h1366} 1366w, ${h1920} 1920w`,
    imageSizes: "100vw",
  };
}